import classnames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { TooltipTrigger, TooltipContent, Tooltip } from '../Tooltip';
import { Separator } from '../Separator';

/**
 * SidePanel component properties.
 * Note that the component monitors changes to the various widths and border sizes and will resize dynamically
 * @property {boolean} isExpanded - boolean indicating if the side panel is expanded/open or collapsed
 * @property {number} expandedWidth - the width of this side panel when expanded not including any borders or margins
 * @property {number} collapsedWidth - the width of this side panel when collapsed not including any borders or margins
 * @property {number} expandedInsideBorderSize - the width of the space between the expanded side panel content and viewport grid
 * @property {number} collapsedInsideBorderSize - the width of the space between the collapsed side panel content and the viewport grid
 * @property {number} collapsedOutsideBorderSize - the width of the space between the collapsed side panel content and the edge of the browser window
 */
export type SidePanelVariant = 'default' | 'light';

type SidePanelProps = {
  side: 'left' | 'right';
  className?: string;
  activeTabIndex: number;
  onOpen: () => void;
  onClose: () => void;
  onActiveTabIndexChange: (payload: { activeTabIndex: number }) => void;
  isExpanded: boolean;
  expandedWidth: number;
  collapsedWidth: number;
  expandedInsideBorderSize: number;
  collapsedInsideBorderSize: number;
  collapsedOutsideBorderSize: number;
  tabs: any;
  /** Light medical chrome for embedded / revamp viewers */
  variant?: SidePanelVariant;
  /** Gray subtitle under “Study Series” on the open left panel (e.g. “3 Series • 245 Images”) */
  leftHeaderSummary?: string;
};

type StyleMap = {
  open: {
    left: {
      marginLeft: string; // the space between the expanded/open left side panel and the browser window left edge
      marginRight: string; // the space between the expanded/open left side panel and the viewport grid
    };
    right: {
      marginLeft: string; // the space between the expanded/open right side panel and the viewport grid
      marginRight: string; // the space between the expanded/open right side panel and the browser window right edge
    };
  };
  closed: {
    left: {
      marginLeft: string; // the space between the collapsed/closed left panel and the browser window left edge
      marginRight: string; // the space between the collapsed/closed left panel and the viewport grid
      alignItems: 'flex-end'; // the flexbox layout align-items property
    };
    right: {
      marginLeft: string; // the space between the collapsed/closed right panel and the viewport grid
      marginRight: string; // the space between the collapsed/closed right panel and the browser window right edge
      alignItems: 'flex-start'; // the flexbox layout align-items property
    };
  };
};
const closeIconWidth = 30;
const gridHorizontalPadding = 10;
const tabSpacerWidth = 2;

const getRootClasses = (variant: SidePanelVariant, side: 'left' | 'right') =>
  classnames('justify-start box-content flex flex-col', {
    'border-black bg-black': variant === 'default',
    'border-viewer-border border-solid bg-white': variant === 'light',
    /* Cast away from the viewport mat so a soft shadow is not read as a “gap” beside the image. */
    'border-r shadow-[-8px_0_20px_-6px_rgba(0,0,0,0.07)]': variant === 'light' && side === 'left',
    'border-l shadow-[8px_0_20px_-6px_rgba(0,0,0,0.07)]': variant === 'light' && side === 'right',
  });

const openStateIconName = {
  left: 'SidePanelCloseLeft',
  right: 'SidePanelCloseRight',
};

const getTabWidth = (numTabs: number) => {
  if (numTabs < 3) {
    return 68;
  } else {
    return 40;
  }
};

const getGridWidth = (numTabs: number, gridAvailableWidth: number) => {
  const spacersWidth = (numTabs - 1) * tabSpacerWidth;
  const tabsWidth = getTabWidth(numTabs) * numTabs;

  if (gridAvailableWidth > tabsWidth + spacersWidth) {
    return tabsWidth + spacersWidth;
  }

  return gridAvailableWidth;
};

const getNumGridColumns = (numTabs: number, gridWidth: number) => {
  if (numTabs === 1) {
    return 1;
  }

  // Start by calculating the number of tabs assuming each tab was accompanied by a spacer.
  const tabWidth = getTabWidth(numTabs);
  const numTabsWithOneSpacerEach = Math.floor(gridWidth / (tabWidth + tabSpacerWidth));

  // But there is always one less spacer than tabs, so now check if an extra tab with one less spacer fits.
  if (
    (numTabsWithOneSpacerEach + 1) * tabWidth + numTabsWithOneSpacerEach * tabSpacerWidth <=
    gridWidth
  ) {
    return numTabsWithOneSpacerEach + 1;
  }

  return numTabsWithOneSpacerEach;
};

const getTabClassNames = (
  numColumns: number,
  numTabs: number,
  tabIndex: number,
  isActiveTab: boolean,
  isTabDisabled: boolean,
  variant: SidePanelVariant
) =>
  classnames('mb-[2px] h-[28px] w-[var(--side-panel-tab-width)] cursor-pointer', {
    'bg-black text-white': variant === 'default',
    /* Match collapsed arrow / left chrome: black glyphs on light panel (not teal primary). */
    'bg-viewer-chrome text-[#737373]': variant === 'light',
    'hover:text-primary': variant === 'default' && !isActiveTab && !isTabDisabled,
    'hover:text-[#0f91b2]': variant === 'light' && !isActiveTab && !isTabDisabled,
    'rounded-l': tabIndex % numColumns === 0,
    'rounded-r': (tabIndex + 1) % numColumns === 0 || tabIndex === numTabs - 1,
  });

const getTabGridStyle = (numTabs: number): CSSProperties => ({
  ['--side-panel-tab-width' as string]: `${getTabWidth(numTabs)}px`,
});

const getTabIconClassNames = (isActiveTab: boolean, variant: SidePanelVariant) => {
  return classnames('flex h-full w-full items-center justify-center', {
    'rounded bg-customblue-40': isActiveTab && variant === 'default',
    'rounded bg-viewer-accent/20': isActiveTab && variant === 'light',
  });
};
const createStyleMap = (
  expandedWidth: number,
  expandedInsideBorderSize: number,
  collapsedWidth: number,
  collapsedInsideBorderSize: number,
  collapsedOutsideBorderSize: number
): StyleMap => {
  const collapsedHideWidth = expandedWidth - collapsedWidth - collapsedOutsideBorderSize;

  return {
    open: {
      left: { marginLeft: '0px', marginRight: `${expandedInsideBorderSize}px` },
      right: { marginLeft: `${expandedInsideBorderSize}px`, marginRight: '0px' },
    },
    closed: {
      left: {
        marginLeft: `-${collapsedHideWidth}px`,
        marginRight: `${collapsedInsideBorderSize}px`,
        alignItems: `flex-end`,
      },
      right: {
        marginLeft: `${collapsedInsideBorderSize}px`,
        marginRight: `-${collapsedHideWidth}px`,
        alignItems: `flex-start`,
      },
    },
  };
};

const getToolTipContent = (label: string, disabled: boolean, variant: SidePanelVariant) => {
  return (
    <>
      <div className="font-normal leading-snug text-white/90">{label}</div>
      {disabled && (
        <div
          className={
            variant === 'light'
              ? 'text-viewer-ink-muted font-normal'
              : 'font-normal text-white/80'
          }
        >
          {'Not available based on current context'}
        </div>
      )}
    </>
  );
};

const createBaseStyle = (expandedWidth: number, variant: SidePanelVariant) => {
  const legacyViewportOffset =
    variant === 'default'
      ? {
          // Legacy dark viewer: nudge to match viewport grid chrome.
          top: '0.2%' as const,
          height: '99.8%' as const,
        }
      : {
          // Light RIS / Figma: flush to toolbar — no gap or misalignment at the top rail.
          top: 0,
          height: '100%',
        };
  return {
    maxWidth: `${expandedWidth}px`,
    width: `${expandedWidth}px`,
    position: 'relative',
    ...legacyViewportOffset,
  };
};

const SidePanel = ({
  side,
  className,
  activeTabIndex: activeTabIndexProp,
  isExpanded,
  tabs,
  onOpen,
  onClose,
  onActiveTabIndexChange,
  expandedWidth = 280,
  collapsedWidth = 25,
  expandedInsideBorderSize = 4,
  collapsedInsideBorderSize = 8,
  collapsedOutsideBorderSize = 4,
  variant = 'default',
  leftHeaderSummary,
}: SidePanelProps) => {
  const { t } = useTranslation('PatientView');
  const [panelOpen, setPanelOpen] = useState(isExpanded);
  const [activeTabIndex, setActiveTabIndex] = useState(activeTabIndexProp ?? 0);

  const styleMap = useMemo(
    () =>
      createStyleMap(
        expandedWidth,
        expandedInsideBorderSize,
        collapsedWidth,
        collapsedInsideBorderSize,
        collapsedOutsideBorderSize
      ),
    [
      expandedWidth,
      expandedInsideBorderSize,
      collapsedWidth,
      collapsedInsideBorderSize,
      collapsedOutsideBorderSize,
    ]
  );

  const baseStyle = useMemo(() => createBaseStyle(expandedWidth, variant), [expandedWidth, variant]);

  const gridAvailableWidth = useMemo(() => {
    return side === 'left'
      ? expandedWidth - 2 * gridHorizontalPadding
      : expandedWidth - closeIconWidth - gridHorizontalPadding;
  }, [expandedWidth, side]);

  const gridWidth = useMemo(() => getGridWidth(tabs.length, gridAvailableWidth), [
    tabs.length,
    gridAvailableWidth,
  ]);
  const openStatus = panelOpen ? 'open' : 'closed';
  const style = Object.assign({}, styleMap[openStatus][side], baseStyle);

  const updatePanelOpen = useCallback(
    (isOpen: boolean) => {
      setPanelOpen(isOpen);
      if (isOpen !== panelOpen) {
        // only fire events for changes
        if (isOpen && onOpen) {
          onOpen();
        } else if (onClose && !isOpen) {
          onClose();
        }
      }
    },
    [panelOpen, onOpen, onClose]
  );

  const updateActiveTabIndex = useCallback(
    (activeTabIndex: number, forceOpen: boolean = false) => {
      if (forceOpen) {
        updatePanelOpen(true);
      }

      setActiveTabIndex(activeTabIndex);

      if (onActiveTabIndexChange) {
        onActiveTabIndexChange({ activeTabIndex });
      }
    },
    [onActiveTabIndexChange, updatePanelOpen]
  );

  useEffect(() => {
    updatePanelOpen(isExpanded);
  }, [isExpanded, updatePanelOpen]);

  useEffect(() => {
    updateActiveTabIndex(activeTabIndexProp ?? 0);
  }, [activeTabIndexProp, updateActiveTabIndex]);

  const getCloseStateComponent = () => {
    const _childComponents = Array.isArray(tabs) ? tabs : [tabs];
    return (
      <>
        <button
          type="button"
          className={classnames(
            'flex h-[28px] w-full cursor-pointer items-center rounded-md p-0',
            variant === 'default' && 'border-0 bg-secondary-dark',
            variant === 'light' && 'border-viewer-border border bg-white shadow-sm shadow-black/5',
            side === 'left' ? 'justify-end pr-2' : 'justify-start pl-2'
          )}
          onClick={() => {
            updatePanelOpen(!panelOpen);
          }}
          aria-expanded={panelOpen}
          aria-label={side === 'left' ? 'Expand series panel' : 'Expand side panel'}
          data-cy={`side-panel-header-${side}`}
        >
          <Icons.NavigationPanelReveal
            className={classnames(
              variant === 'light' ? 'text-[#737373]' : 'text-primary',
              side === 'left' && 'rotate-180 transform'
            )}
          />
        </button>
        <div className={classnames('mt-3 flex flex-col space-y-3')}>
          {_childComponents.map((childComponent, index) => (
            <Tooltip key={index}>
              <TooltipTrigger>
                <button
                  type="button"
                  id={`${childComponent.name}-btn`}
                  data-cy={`${childComponent.name}-btn`}
                  className={classnames(
                    'border-0 bg-transparent p-0 hover:cursor-pointer',
                    variant === 'light' ? 'text-[#737373]' : 'text-primary'
                  )}
                  disabled={!!childComponent.disabled}
                  aria-label={childComponent.label || childComponent.name}
                  onClick={() => {
                    return childComponent.disabled ? null : updateActiveTabIndex(index, true);
                  }}
                >
                  {React.createElement(Icons[childComponent.iconName] || Icons.MissingIcon, {
                    className: classnames('h-[22px] w-[22px]', {
                      'text-[#737373]': variant === 'light',
                      'text-primary': variant === 'default',
                      'ohif-disabled': childComponent.disabled,
                    }),
                  })}
                </button>
              </TooltipTrigger>
              <TooltipContent side={side === 'left' ? 'right' : 'left'}>
                <div
                  className={classnames(
                    'flex items-center',
                    side === 'left' ? 'justify-end' : 'justify-start'
                  )}
                >
                  {getToolTipContent(childComponent.label, childComponent.disabled, variant)}
                </div>
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      </>
    );
  };

  const getCloseIcon = (asButton = true) => {
    const icon = React.createElement(Icons[openStateIconName[side]] || Icons.MissingIcon, {
      className: variant === 'light' ? 'text-[#737373]' : 'text-primary',
    });
    const positionClass = classnames(
      'absolute flex w-[30px] items-center justify-center',
      side === 'left' ? 'right-0' : 'left-0'
    );

    if (!asButton) {
      return (
        <span className={positionClass} aria-hidden="true">
          {icon}
        </span>
      );
    }

    return (
      <button
        type="button"
        className={classnames(positionClass, 'cursor-pointer border-0 bg-transparent p-0')}
        onClick={() => {
          updatePanelOpen(!panelOpen);
        }}
        aria-expanded={panelOpen}
        aria-label={side === 'left' ? 'Collapse series panel' : 'Collapse side panel'}
        data-cy={`side-panel-header-${side}`}
      >
        {icon}
      </button>
    );
  };

  const getLeftChromeCloseControl = () => (
    <button
      type="button"
      className="flex h-[30px] w-[30px] shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0"
      onClick={() => updatePanelOpen(!panelOpen)}
      aria-expanded={panelOpen}
      aria-label="Collapse series panel"
      data-cy={`side-panel-header-${side}`}
    >
      {React.createElement(Icons[openStateIconName.left] || Icons.MissingIcon, {
        className: variant === 'light' ? 'text-[#737373]' : 'text-primary',
      })}
    </button>
  );

  const getTabGridInner = () => {
    const numCols = getNumGridColumns(tabs.length, gridWidth);

    return (
      <div className={classnames('flex grow justify-center')}>
        <div
          className={classnames('flex flex-wrap', {
            'bg-primary-dark text-primary': variant === 'default',
            'bg-viewer-chrome text-[#737373]': variant === 'light',
          })}
          style={getTabGridStyle(tabs.length)}
        >
          {tabs.map((tab, tabIndex) => {
            const { disabled } = tab;
            return (
              <React.Fragment key={tabIndex}>
                {tabIndex % numCols !== 0 && (
                  <div
                    className={classnames(
                      'flex h-[28px] w-[2px] items-center',
                      variant === 'default' ? 'bg-black' : 'bg-viewer-border'
                    )}
                  >
                    <div
                      className={classnames('h-[20px] w-full', {
                        'bg-primary-dark': variant === 'default',
                        'bg-viewer-chrome': variant === 'light',
                      })}
                    />
                  </div>
                )}
                <Tooltip key={tabIndex}>
                  <TooltipTrigger>
                    <button
                      type="button"
                      className={classnames(
                        getTabClassNames(
                          numCols,
                          tabs.length,
                          tabIndex,
                          tabIndex === activeTabIndex,
                          disabled,
                          variant
                        ),
                        'border-0 p-0'
                      )}
                      disabled={!!disabled}
                      aria-label={tab.label || tab.name}
                      aria-pressed={tabIndex === activeTabIndex}
                      onClick={() => {
                        return disabled ? null : updateActiveTabIndex(tabIndex);
                      }}
                      data-cy={`${tab.name}-btn`}
                    >
                      <div className={getTabIconClassNames(tabIndex === activeTabIndex, variant)}>
                        {React.createElement(Icons[tab.iconName] || Icons.MissingIcon, {
                          className: classnames('h-[22px] w-[22px]', {
                            'text-[#737373]': variant === 'light',
                            'text-primary': variant === 'default',
                            'ohif-disabled': disabled,
                          }),
                        })}
                      </div>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    {getToolTipContent(tab.label, disabled, variant)}
                  </TooltipContent>
                </Tooltip>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  };

  const getTabGridComponent = () => {
    return (
      <>
        {getCloseIcon()}
        {getTabGridInner()}
      </>
    );
  };

  const getOneTabComponent = () => {
    return (
      <button
        type="button"
        className="relative flex grow cursor-pointer select-none justify-center self-center border-0 bg-transparent p-0 text-[13px] font-semibold"
        data-cy={`${tabs[0].name}-btn`}
        aria-expanded={panelOpen}
        aria-label={tabs[0].label || 'Toggle side panel'}
        onClick={() => updatePanelOpen(!panelOpen)}
      >
        {getCloseIcon(false)}
        <span className={variant === 'light' ? 'text-black' : 'text-primary'}>{tabs[0].label}</span>
      </button>
    );
  };

  const getOpenStateComponent = () => {
    if (side === 'left') {
      return (
        <>
          <div
            className={classnames(
              'flex w-full flex-shrink-0 select-none flex-col rounded-t',
              variant === 'default' && 'bg-bkg-med border-b border-black',
              variant === 'light' && 'border-viewer-border border-b bg-white'
            )}
          >
            <div className="flex w-full items-center justify-between gap-2 px-2 pb-1.5 pt-2.5">
              <div className="min-w-0 flex-1 pr-1">
                <div
                  className={classnames(
                    'text-[13px] font-bold leading-tight tracking-wide',
                    variant === 'light' ? 'text-black' : 'text-white'
                  )}
                >
                  {t('StudySeries')}
                </div>
                {leftHeaderSummary ? (
                  <div
                    className={classnames(
                      'mt-0.5 text-[11px] font-normal leading-[15px]',
                      variant === 'light' ? 'text-[#757575]' : 'text-neutral-400'
                    )}
                  >
                    {leftHeaderSummary}
                  </div>
                ) : null}
              </div>
              {getLeftChromeCloseControl()}
            </div>
            {tabs.length > 1 ? (
              <div
                className={classnames(
                  'relative flex justify-center border-t px-2 pb-2 pt-2',
                  variant === 'light' ? 'border-viewer-border' : 'border-black'
                )}
              >
                {getTabGridInner()}
              </div>
            ) : null}
          </div>
        </>
      );
    }

    if (variant === 'light' && tabs.length > 1) {
      const segmentLabel = (tab: { label?: string; iconLabel?: string; name?: string }) => {
        const raw = tab.iconLabel || tab.label || tab.name || '';
        if (raw === 'Segmentation') {
          return 'Segmentations';
        }
        if (raw === 'Measurement') {
          return 'Measurements';
        }
        return raw;
      };

      return (
        <>
          <div
            className={classnames(
              'flex h-16 w-full flex-shrink-0 select-none items-center gap-2 border-b border-[#e5e5e5] bg-white px-2'
            )}
          >
            <button
              type="button"
              className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-[5.2px] text-[#737373] transition-colors hover:bg-black/[0.04] hover:text-[#0a0a0a]"
              onClick={() => updatePanelOpen(false)}
              aria-label="Collapse side panel"
              data-cy="side-panel-header-right"
            >
              {React.createElement(Icons[openStateIconName.right] || Icons.MissingIcon, {
                className: 'h-4 w-4',
              })}
            </button>
            <div className="flex min-w-0 flex-1 justify-center overflow-hidden px-1">
              <div className="inline-flex max-w-full items-center gap-1 rounded-[6px] bg-[#f5f5f5] p-1">
                {tabs.map((tab, tabIndex) => {
                  const { disabled } = tab;
                  const isActive = tabIndex === activeTabIndex;
                  return (
                    <Tooltip key={tabIndex}>
                      <TooltipTrigger>
                        <button
                          type="button"
                          disabled={disabled}
                          data-cy={`${tab.name}-btn`}
                          className={classnames(
                            'min-h-[34px] shrink-0 cursor-pointer rounded-[5px] px-4 py-2 text-center text-[14px] font-medium leading-5 tracking-[0.01em] transition-shadow',
                            disabled && 'cursor-not-allowed opacity-40',
                            isActive
                              ? 'bg-white font-semibold text-[#0a0a0a] shadow-[0_1px_3px_rgba(0,0,0,0.1),0_1px_2px_rgba(0,0,0,0.1)]'
                              : 'bg-transparent font-medium text-[#737373] hover:text-[#0a0a0a]'
                          )}
                          onClick={() => (disabled ? null : updateActiveTabIndex(tabIndex))}
                        >
                          {segmentLabel(tab)}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        {getToolTipContent(tab.label, !!disabled, variant)}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <div
          className={classnames(
            'relative flex h-[40px] flex-shrink-0 select-none rounded-t p-2',
            variant === 'default' && 'bg-bkg-med',
            variant === 'light' && 'border-viewer-border border-b bg-white'
          )}
        >
          {tabs.length === 1 ? getOneTabComponent() : getTabGridComponent()}
        </div>
        <Separator
          orientation="horizontal"
          className={variant === 'light' ? 'bg-viewer-border' : 'bg-black'}
          thickness="2px"
        />
      </>
    );
  };

  return (
    <div
      data-ohif-side-panel={variant}
      className={classnames(className, getRootClasses(variant, side))}
      style={style as CSSProperties}
    >
      {panelOpen ? (
        <>
          {getOpenStateComponent()}
          {tabs.map((tab, tabIndex) => {
            if (tabIndex !== activeTabIndex) {
              return null;
            }
            const PanelContent = tab.content as React.ComponentType<Record<string, unknown>>;
            return (
              <div
                key={tabIndex}
                className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
              >
                <PanelContent />
              </div>
            );
          })}
        </>
      ) : (
        <React.Fragment>{getCloseStateComponent()}</React.Fragment>
      )}
    </div>
  );
};

export { SidePanel };
